<script setup>
import { ref, nextTick } from "vue";

const props = defineProps({
  issue: {},
})
const showDetailedFix = ref(false)
const detailedFixContainer = ref(null)

const copyToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here if available
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  } else {
    // Fallback
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback copy failed', err);
    }
    document.body.removeChild(textArea);
  }
}

const handleToggle = async () => {
  showDetailedFix.value = !showDetailedFix.value
  if (showDetailedFix.value) {
    await nextTick()
    if (detailedFixContainer.value) {
      const codeBlocks = detailedFixContainer.value.querySelectorAll('.codehilite pre code')
      codeBlocks.forEach(code => {
        if (code.querySelector('.copy-btn')) return

        code.style.position = 'relative'
        code.style.display = 'block' // Ensure it's a block for the copy button positioning

        const btn = document.createElement('button')
        btn.innerHTML = '<i class="fa fa-copy"></i>'
        btn.className = 'btn btn-sm btn-light copy-btn'
        btn.style.position = 'absolute'
        btn.style.top = '5px'
        btn.style.right = '5px'
        btn.style.padding = '2px 6px'
        btn.style.fontSize = '12px'
        btn.style.lineHeight = '1'
        btn.title = 'Copy code'
        
        btn.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          // Get text excluding the button text
          const textToCopy = Array.from(code.childNodes)
            .filter(node => node !== btn)
            .map(node => node.textContent)
            .join('')
            .trim()
          copyToClipboard(textToCopy)
          
          const icon = btn.querySelector('i')
          icon.className = 'fa fa-check text-success'
          setTimeout(() => {
            icon.className = 'fa fa-copy'
          }, 2000)
        }
        code.appendChild(btn)
      })
    }
  }
}
</script>

<template>
  <div v-if="issue.detailed_fix">
    <a v-if="!showDetailedFix" href="#" class="small text-decoration-none"
       @click.stop.prevent="handleToggle">
      View detailed fix <i class="fa fa-chevron-down small"></i>
    </a>
    <Transition name="fade">
      <div v-if="showDetailedFix" class="mt-2 p-3 detailed-fix-box rounded small">
        <div ref="detailedFixContainer" class="detailed-fix-content" v-html="issue.detailed_fix"></div>
        <div class="mt-3 text-end border-top pt-2">
          <a href="#" class="text-muted text-decoration-none small" @click.stop.prevent="handleToggle">
            <i class="fa fa-chevron-up me-1"></i>Hide
          </a>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>


.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.detailed-fix-content :deep(.codehilite) {
  margin-bottom: 1rem;
}

.detailed-fix-content :deep(.codehilite pre) {
  margin-bottom: 0;
  padding: 0;
}

:deep(code) {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.9em;
  color: #d63384;
}

.detailed-fix-content :deep(.codehilite code) {
  display: block;
  padding: 1rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.detailed-fix-box {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-left: 4px solid #0d6efd;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.detailed-fix-content :deep(p) {
  margin-bottom: 0.75rem;
}

.detailed-fix-content :deep(p:last-child) {
  margin-bottom: 0;
}

.detailed-fix-content :deep(ul), .detailed-fix-content :deep(ol) {
  padding-left: 1.25rem;
  margin-bottom: 0.75rem;
}

.detailed-fix-content :deep(li) {
  margin-bottom: 0.25rem;
}

:deep(.copy-btn) {
  opacity: 0.6;
  transition: opacity 0.2s;
  border: 1px solid #ddd;
}

:deep(.copy-btn:hover) {
  opacity: 1;
}
</style>